import { useCallback, useEffect, useRef, useState } from 'react';

export type UseVoiceInputParams = {
  text: string;
  setText: (value: string) => void;
  onCommit?: () => void;
};

export function useVoiceInput({
  text,
  setText,
  onCommit,
}: UseVoiceInputParams) {
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechListening, setSpeechListening] = useState(false);
  const [voiceChatActive, setVoiceChatActive] = useState(false);

  const speechRecognitionRef = useRef<any>(null);
  const speechBaseTextRef = useRef('');
  const speechAutoSendTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speechListeningRef = useRef(false);
  const voiceChatActiveRef = useRef(false);
  const lastSpeechResultAtRef = useRef(0);
  const commitOnEndRef = useRef(false);

  const getSpeechRecognitionCtor = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const w = window as any;
    return w.SpeechRecognition || w.webkitSpeechRecognition || null;
  }, []);

  useEffect(() => {
    speechListeningRef.current = speechListening;
  }, [speechListening]);

  useEffect(() => {
    voiceChatActiveRef.current = voiceChatActive;
  }, [voiceChatActive]);

  const clearSpeechAutoSend = useCallback(() => {
    if (speechAutoSendTimeoutRef.current) {
      clearTimeout(speechAutoSendTimeoutRef.current);
      speechAutoSendTimeoutRef.current = null;
    }
  }, []);

  const resetSpeechBuffers = useCallback(() => {
    speechBaseTextRef.current = '';
  }, []);

  const tryCommit = useCallback(() => {
    if (!onCommit) return;
    if (!text.trim()) return;
    onCommit();
  }, [onCommit, text]);

  const stopVoiceChat = useCallback((commit: boolean) => {
    const recognition = speechRecognitionRef.current;
    voiceChatActiveRef.current = false;
    setVoiceChatActive(false);
    if (commit) {
      commitOnEndRef.current = true;
    }
    clearSpeechAutoSend();
    if (recognition) {
      try {
        recognition.stop();
      } catch {
        // ignore stop errors
      }
    }
  }, [clearSpeechAutoSend]);

  useEffect(() => {
    const SpeechRecognitionCtor = getSpeechRecognitionCtor();
    if (!SpeechRecognitionCtor) {
      setSpeechSupported(false);
      return;
    }
    setSpeechSupported(true);

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'de-DE';
    recognition.onstart = () => setSpeechListening(true);
    recognition.onend = () => {
      setSpeechListening(false);
      if (commitOnEndRef.current) {
        commitOnEndRef.current = false;
        tryCommit();
      }
      if (voiceChatActiveRef.current) {
        setTimeout(() => {
          try {
            recognition.start();
          } catch {
            // ignore restart errors
          }
        }, 200);
      } else {
        clearSpeechAutoSend();
      }
    };
    recognition.onerror = (event: any) => {
      console.warn('Speech recognition error', event);
      setSpeechListening(false);
      if (voiceChatActiveRef.current) {
        setTimeout(() => {
          try {
            recognition.start();
          } catch {
            // ignore restart errors
          }
        }, 400);
      } else {
        clearSpeechAutoSend();
      }
    };
    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript ?? '';
        if (result.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const base = speechBaseTextRef.current.trim();
      const finalText = finalTranscript.trim();
      const interimText = interimTranscript.trim();
      const nextBase = finalText ? [base, finalText].filter(Boolean).join(' ') : base;
      if (finalText) {
        speechBaseTextRef.current = nextBase;
      }
      const displayText = [nextBase, interimText].filter(Boolean).join(' ');
      setText(displayText);
      lastSpeechResultAtRef.current = Date.now();
      if (voiceChatActiveRef.current) {
        clearSpeechAutoSend();
        speechAutoSendTimeoutRef.current = setTimeout(() => {
          if (!voiceChatActiveRef.current) return;
          const silenceMs = Date.now() - (lastSpeechResultAtRef.current || 0);
          if (silenceMs < 3000) return;
          stopVoiceChat(true);
        }, 3100);
      }
    };

    speechRecognitionRef.current = recognition;
    return () => {
      recognition.onstart = null;
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onresult = null;
      try {
        recognition.stop();
      } catch {
        // ignore stop errors during cleanup
      }
      clearSpeechAutoSend();
      speechRecognitionRef.current = null;
    };
  }, [clearSpeechAutoSend, getSpeechRecognitionCtor, setText, stopVoiceChat, tryCommit]);

  useEffect(() => {
    if (!voiceChatActive) return;
    const interval = setInterval(() => {
      const recognition = speechRecognitionRef.current;
      if (!recognition) return;
      if (!speechListeningRef.current) {
        try {
          recognition.start();
        } catch {
          // ignore restart errors
        }
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [voiceChatActive]);

  const toggleSpeech = useCallback(() => {
    if (!speechSupported) return;
    if (voiceChatActiveRef.current) return;
    const recognition = speechRecognitionRef.current;
    if (!recognition) return;
    if (speechListening) {
      commitOnEndRef.current = true;
      recognition.stop();
      return;
    }
    speechBaseTextRef.current = text.trim();
    try {
      recognition.start();
    } catch (err) {
      console.warn('Speech recognition start failed', err);
    }
  }, [speechListening, speechSupported, text]);

  const toggleVoiceChat = useCallback(() => {
    if (!speechSupported) return;
    const recognition = speechRecognitionRef.current;
    if (!recognition) return;
    if (voiceChatActiveRef.current) {
      stopVoiceChat(true);
      return;
    }
    voiceChatActiveRef.current = true;
    setVoiceChatActive(true);
    speechBaseTextRef.current = text.trim();
    if (!speechListeningRef.current) {
      try {
        recognition.start();
      } catch (err) {
        console.warn('Speech recognition start failed', err);
      }
    }
  }, [speechSupported, stopVoiceChat, text]);

  return {
    speechSupported,
    speechListening,
    voiceChatActive,
    toggleSpeech,
    toggleVoiceChat,
    resetSpeechBuffers,
  };
}
